import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  pdApi,
  type PdProject,
  type PdUser,
  type PdSavedView,
  type PdFramework,
} from '../api';
import { HelpPanel, type HelpContent } from '../HelpPanel';
import '../screens.css';
import './ProjectRegistry.css';

/**
 * Projects → Project Registry.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/projects.ejs` and its
 * create/edit form.
 *
 * `budget` IS READ-ONLY HERE, and that is not an oversight to fix. It is a roll-up of the
 * child opportunities' budgets: the source's create form pins it to 0 and its update
 * handler deliberately omits the column (routes/index.js:3925 says so in as many words).
 * The server recomputes it whenever an opportunity's budget or project link changes. A
 * writable field would be overwritten within one edit and would mislead until it was.
 *
 * STATUS IS THE GUARDED FIELD. Only an owner or an administrator may change it, and
 * moving to Complete is refused while any framework-relevant opportunity is unscored —
 * the server returns the list of offenders, which is surfaced verbatim.
 */

interface EditState {
  /**
   * `view` is read-only and offers no Save — the owner's ruling that "view is only view
   * and should not provide any data change". The pencil in the row is how you get to
   * `edit`, which is how the canonical APR drawer works too.
   */
  mode: 'view' | 'edit';
  isNew: boolean;
  project_id: string;
  project_description: string;
  additional_description: string;
  status: string;
  created_by: number | '';
  project_owner: string;
  effective_from_date: string;
  effective_to_date: string;
  methodology: string;
  /* Free-form classification. The source gives it its OWN section — see the panel. */
  category: string;
  /* The scoring framework governing this project's opportunities. Owner-directed,
     2026-08-10 — assignable only while the project is Active, and only from frameworks
     that are Design Complete. */
  framework_id: string;
}

const EMPTY: EditState = {
  mode: 'edit',
  isNew: true,
  project_id: '',
  project_description: '',
  additional_description: '',
  status: 'Created',
  created_by: '',
  project_owner: '',
  effective_from_date: '',
  effective_to_date: '',
  methodology: 'Waterfall',
  category: '',
  framework_id: '',
};

/**
 * `user_saved_views.screen_name` for this screen — the SOURCE's own key, so a view saved
 * in the source app still resolves here.
 */
const SCREEN_NAME = 'projects-list';

/**
 * The source's card-size slider on THIS screen: 240–680 in steps of 20, default 300 —
 * wider than the Opportunity Registry's, because a project card carries six progress bars.
 * The chosen width persists under the source's own key, `projectsCardSize`.
 */
const CARD_MIN = 240;
const CARD_MAX = 680;
const CARD_STEP = 20;
const CARD_DEFAULT = 300;

/** The card header's status pill — background and text, the source's own pairs. */
const CARD_STATUS: Record<string, { bg: string; fg: string }> = {
  Created: { bg: '#e3f2fd', fg: '#1976d2' },
  Active: { bg: '#e8f5e9', fg: '#388e3c' },
  Complete: { bg: '#e0f2f1', fg: '#00796b' },
};

/**
 * A progress bar's colour, by how far along it is. The source's four bands, and the reason
 * a nearly-empty bar reads red at a glance rather than needing its numbers read.
 */
function barColour(pct: number): string {
  if (pct <= 25) return '#c62828';
  if (pct <= 50) return '#fbc02d';
  if (pct <= 75) return '#66bb6a';
  return '#2e7d32';
}

/**
 * The source's currency shorthand for the budget bar: $1.2M, $90K, $500.
 * Deliberately NOT `toLocaleString` — the bar's label has room for four characters.
 */
function shortMoney(raw: unknown): string {
  const v = Number(raw) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

const shortDate = (d: unknown) =>
  d
    ? new Date(String(d)).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: '2-digit',
      })
    : null;

/** One project's rolled-up counts, from `/projects/progress-stats`. */
interface ProgressStat {
  project_id: string;
  total_opportunities: number;
  approved_count: number;
  scored_count: number;
  committed_count: number;
  in_development_count: number;
  delivered_count: number;
  relevant_count: number;
  project_budget: number | null;
  budget_utilized: number | null;
}

const HELP: HelpContent = {
  title: 'Project Registry',
  icon: '📁',
  purpose:
    "A <strong>project</strong> is the highest-level grouping an opportunity belongs to \u2014 a logical container bundling related opportunities under one owner and framework (a business unit, geography or function). This screen is where projects are created, owned, dated and tracked.",
  doHere: [
    "<strong>Add a project</strong> manually, or <strong>bulk-import from Excel</strong> using the downloadable template.",
    "Switch between <strong>Cards</strong> and <strong>Details</strong>, and filter by project or search on id and description.",
    "Watch each project's <strong>progress</strong> \u2014 how many of its opportunities are approved, scored, committed, in development and delivered, and how much of its budget is used.",
    "<strong>Download projects</strong> to Excel for reporting.",
  ],
  fits: 'Discover &rarr; <strong>Group into projects</strong> &rarr; Score &rarr; Finance &rarr; Optimize &rarr; Plan &rarr; Execute',
  firstTime:
    "Click <strong>Add Project</strong>, give it an id, a description and an owner, then capture opportunities against it on the <strong>Opportunity Registry</strong>.",
  sections: [
    {
      label: 'What the progress bars mean',
      type: 'defs',
      items: [
        { term: 'Approved', color: '#1976d2', def: 'Opportunities that have reached <strong>Approved</strong> or any later stage. The bars are <em>cumulative</em>, so each one counts everything at that stage and beyond.' },
        { term: 'Scored', color: '#00897b', def: 'Scored, In Development or Delivered \u2014 counting only opportunities their framework considers relevant.' },
        { term: 'Committed', color: '#5e35b1', def: 'Committed to delivery: In Development or Delivered.' },
        { term: 'Delivered', color: '#00796b', def: 'Finished and handed over.' },
        { term: 'Budget', color: '#f57c00', def: 'Actual spend from the Budget Tracker against the project budget \u2014 not a forecast.' },
      ],
      note: "A bar is <strong>red below 25%</strong>, amber to 50%, light green to 75% and dark green above \u2014 so a project that has barely started reads as such without reading the numbers.",
    },
  ],
  links: [{ label: 'Full guide: how the platform works', href: '/portfolio_definition/user-guide' }],
};

const STATUS_COLOURS: Record<string, string> = {
  Created: '#64748b',
  Active: '#166534',
  Complete: '#1e40af',
};

/**
 * One segmented progress bar.
 *
 * THE THREE VARIANTS ARE THE SOURCE'S THREE FUNCTIONS, and they do not share their maths.
 *
 * `count` (updateProgressBar) — the blocks are a COUNT, not a percentage width: at most 20,
 * each standing for `ceil(total/20)` opportunities when there are more than 20, and
 * `ceil(count/segmentsPerBlock)` of them lit. A project with 8 opportunities shows 8 blocks;
 * one with 200 still shows 20. The lit colour comes from the four bands, so a barely-started
 * project reads red without its numbers being read.
 *
 * `money` (updateBudgetBar) — ALWAYS 20 blocks, lit by `round(pct/100 * 20)`, and ALWAYS
 * green (#2e7d32) whatever the percentage. Budget utilisation is not a completion measure,
 * so the red-to-green bands would say the wrong thing about it.
 *
 * `na` (greyOutScoredBar) — the Scored bar when the framework marks nothing relevant to
 * score. Not zero-of-zero: "N/A" at 55% opacity, because there is nothing to score rather
 * than nothing scored.
 *
 * An empty `count` bar is a single grey block; an empty `money` bar is twenty. That is the
 * source's own asymmetry, not a slip here.
 */
function ProgressTrack({
  label,
  count,
  total,
  money,
  na,
}: {
  label: string;
  count: number;
  total: number;
  money?: boolean;
  na?: boolean;
}) {
  let blocks: string[];
  if (na) {
    blocks = ['#e0e0e0'];
  } else if (money) {
    const pct = total > 0 ? Math.min((count / total) * 100, 100) : 0;
    const active = total > 0 ? Math.round((pct / 100) * 20) : 0;
    blocks = Array.from({ length: 20 }, (_, i) => (i < active ? '#2e7d32' : '#e0e0e0'));
  } else if (total > 0) {
    const colour = barColour((count / total) * 100);
    const maxSegments = Math.min(total, 20);
    const perBlock = total > 20 ? Math.ceil(total / 20) : 1;
    const active = Math.ceil(count / perBlock);
    blocks = Array.from({ length: maxSegments }, (_, i) => (i < active ? colour : '#e0e0e0'));
  } else {
    blocks = ['#e0e0e0'];
  }

  const readout = na
    ? 'N/A'
    : money
      ? `${shortMoney(count)} / ${shortMoney(total)}`
      : `${count} / ${total}`;
  const end = na ? 'N/A' : money ? shortMoney(total) : String(total);

  return (
    <div className="pd-track" style={na ? { opacity: 0.55 } : undefined}>
      <div className="pd-track-top">
        <span className="pd-track-lbl">{label}</span>
        <span className="pd-track-count">{readout}</span>
      </div>
      <div className="pd-track-segments">
        {blocks.map((c, i) => (
          <div key={i} className="pd-track-seg" style={{ background: c }} />
        ))}
      </div>
      <div className="pd-track-ends">
        <span>{money ? '$0' : '0'}</span>
        <span>{end}</span>
      </div>
    </div>
  );
}

export function ProjectRegistry() {
  const [projects, setProjects] = useState<PdProject[]>([]);
  const [users, setUsers] = useState<PdUser[]>([]);
  /* Every active framework. The panel offers only the Design Complete ones — see
     `completeFrameworks` — but the full list is kept so a project already carrying a
     framework can still show its NAME after that framework is reverted to In Design. */
  const [frameworks, setFrameworks] = useState<PdFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [edit, setEdit] = useState<EditState | null>(null);
  /* The panel's maximised state, as the canonical APR drawer has it. Not persisted. */
  const [maximized, setMaximized] = useState(false);

  /* Cards or Details, remembered — the source's own localStorage keys and defaults. */
  const [viewMode, setViewMode] = useState<'card' | 'details'>(
    () => (localStorage.getItem('projectsViewType') as 'card' | 'details') || 'card',
  );
  const [cardSize, setCardSize] = useState<number>(
    () => Number(localStorage.getItem('projectsCardSize')) || CARD_DEFAULT,
  );

  /* The project multi-select — Ctrl+click for several, as the source's own dropdown. */
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);

  /* Saved views. */
  const [savedViews, setSavedViews] = useState<PdSavedView[]>([]);
  const [currentView, setCurrentView] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewIsDefault, setViewIsDefault] = useState(false);
  const appliedDefault = useRef(false);

  /*
    Delete is administrators-only in the source (`user.role === 'Administrator'`), so the
    server sends the flag with the registry payload.
  */
  const [isAdmin, setIsAdmin] = useState(false);

  /* Per-project roll-ups behind the progress bars. */
  const [stats, setStats] = useState<Record<string, ProgressStat>>({});
  const uploadRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.projects.list();
      setProjects(data.projects);
      setUsers(data.users);
      setFrameworks(data.frameworks ?? []);
      setIsAdmin(Boolean(data.isAdmin));
      /*
        The progress roll-ups come from their own endpoint, as the source does — one
        aggregate query rather than a count per card. Failing to load them leaves the bars
        empty rather than failing the screen: the project list is still useful without them.
      */
      try {
        const ps = await pdApi.projects.progressStats();
        const byId: Record<string, ProgressStat> = {};
        (ps.data ?? []).forEach((r) => {
          const row = r as unknown as ProgressStat;
          byId[String(row.project_id)] = row;
        });
        setStats(byId);
      } catch {
        setStats({});
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return projects.filter((p) => {
      // An EMPTY selection means every project — the source's "Select Projects"
      // placeholder shows everything until you actually pick something.
      if (selectedProjects.length && !selectedProjects.includes(p.project_id)) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (
        q &&
        !p.project_id.toLowerCase().includes(q) &&
        !(p.project_description ?? '').toLowerCase().includes(q) &&
        !(p.additional_description ?? '').toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [projects, filter, statusFilter, selectedProjects]);

  useEffect(() => {
    localStorage.setItem('projectsViewType', viewMode);
  }, [viewMode]);
  useEffect(() => {
    localStorage.setItem('projectsCardSize', String(cardSize));
  }, [cardSize]);

  useEffect(() => {
    if (!projectMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.pd-mdd')) setProjectMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [projectMenuOpen]);

  /** Put a saved view's filters back on the screen. */
  const applyView = useCallback((v: PdSavedView) => {
    try {
      const cfg = JSON.parse(v.filter_config) as { selectedProjects?: string[]; search?: string };
      setSelectedProjects(cfg.selectedProjects ?? []);
      setFilter(cfg.search ?? '');
    } catch {
      setError(`Saved view "${v.view_name}" could not be read.`);
    }
  }, []);

  const loadViews = useCallback(async () => {
    try {
      const d = await pdApi.savedViews.list(SCREEN_NAME);
      setSavedViews(d.views ?? []);
      // A default view applies itself once, on first load — not after every save, which
      // would discard whatever had just been filtered to.
      const def = (d.views ?? []).find((v) => v.is_default === 1);
      if (def && !appliedDefault.current) {
        appliedDefault.current = true;
        applyView(def);
        setCurrentView(String(def.view_id));
      }
    } catch {
      /* an empty dropdown is the honest fallback */
    }
  }, [applyView]);

  useEffect(() => {
    void loadViews();
  }, [loadViews]);

  const saveView = async () => {
    const name = viewName.trim();
    if (!name) {
      setError('Please enter a view name');
      return;
    }
    try {
      const d = await pdApi.savedViews.save({
        screen_name: SCREEN_NAME,
        view_name: name,
        filter_config: JSON.stringify({ selectedProjects, search: filter }),
        is_default: viewIsDefault ? 1 : 0,
      });
      if (!d.success) {
        setError(d.error ?? 'Failed to save view');
        return;
      }
      setSaveOpen(false);
      setViewName('');
      setViewIsDefault(false);
      setNotice('View saved successfully!');
      await loadViews();
    } catch (e) {
      setError(`Error saving view: ${(e as Error).message}`);
    }
  };

  const deleteView = async () => {
    if (!currentView) {
      setError('Select a view to delete');
      return;
    }
    const v = savedViews.find((x) => String(x.view_id) === currentView);
    if (!window.confirm(`Delete the saved view "${v?.view_name ?? ''}"?`)) return;
    try {
      await pdApi.savedViews.remove(Number(currentView));
      setCurrentView('');
      setNotice('View deleted successfully');
      await loadViews();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /** Bulk upload — multipart, so it does not go through the JSON client. */
  const upload = async (file: File) => {
    const fd = new FormData();
    fd.append('excelFile', file);
    setError(null);
    setNotice(null);
    try {
      const r = await fetch('/api/portfolio_definition/projects/upload-excel', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      });
      const d = (await r.json()) as { success?: boolean; error?: string; message?: string };
      if (!r.ok || d.success === false) {
        setError(d.error ?? 'Upload failed');
        return;
      }
      setNotice(d.message ?? 'Projects uploaded.');
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      closePanel();
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /** Closing always drops the maximised state, so the next open starts at its normal width. */
  const closePanel = () => {
    setEdit(null);
    setMaximized(false);
  };

  /** Open the panel on an existing project, read-only or editable. */
  const openPanel = (p: PdProject, mode: 'view' | 'edit') =>
    setEdit({
      mode,
      isNew: false,
      project_id: p.project_id,
      project_description: p.project_description ?? '',
      additional_description: p.additional_description ?? '',
      status: p.status,
      created_by: (p.created_by as number) ?? '',
      project_owner: p.project_owner ?? '',
      effective_from_date: (p.effective_from_date as string)?.slice(0, 10) ?? '',
      effective_to_date: (p.effective_to_date as string)?.slice(0, 10) ?? '',
      methodology: p.methodology ?? 'Waterfall',
      category: (p.category as string) ?? '',
      framework_id: (p.framework_id as string) ?? '',
    });

  /**
   * Delete a project.
   *
   * The card only offers this for a project that is NOT Active — the source's own rule,
   * on the reasoning that a project with delivery under way should not be removable from
   * a list screen.
   */
  /*
   * Only a FINISHED framework may govern a project — the owner's rule. `design_status`
   * carries the source's wording, "Design Complete", which is what the Frameworks screen's
   * Complete action sets.
   *
   * The server checks this again on save; this list is what stops the wrong choice being
   * offered in the first place.
   */
  const completeFrameworks = useMemo(
    () => frameworks.filter((f) => f.design_status === 'Design Complete'),
    [frameworks],
  );

  /* Resolved from the FULL list, not `completeFrameworks`: a project keeps its framework if
     that framework is later reverted to In Design, and the panel must still name it rather
     than showing a bare id. */
  const frameworkName = (id: string) =>
    frameworks.find((f) => f.framework_id === id)?.framework_name ?? id;

  const removeProject = async (id: string) => {
    const pr = projects.find((x) => x.project_id === id);
    const display = pr ? `${id} - ${pr.project_description}` : id;
    if (
      !window.confirm(
        `Delete project ${display}?\n\nDiscovery-generated opportunities will return to ` +
          `Strategic Discoveries for re-selection.\nManually created opportunities will be ` +
          `unassigned from the project but kept in the database.`,
      )
    ) {
      return;
    }
    await run(() => pdApi.projects.remove(id), 'Project deleted.');
  };

  const save = () => {
    if (!edit) return;
    const body = {
      project_id: edit.project_id,
      project_description: edit.project_description,
      additional_description: edit.additional_description,
      status: edit.status,
      created_by: edit.created_by || undefined,
      project_owner: edit.project_owner,
      effective_from_date: edit.effective_from_date || null,
      effective_to_date: edit.effective_to_date || null,
      methodology: edit.methodology,
      category: edit.category || null,
      framework_id: edit.framework_id || null,
    };
    return run(
      () =>
        edit.isNew
          ? pdApi.projects.create(body)
          : pdApi.projects.update(edit.project_id, body),
      edit.isNew ? 'Project created' : 'Project updated',
    );
  };

  /* A new project is always editable; an existing one only in edit mode. */
  const editable = edit != null && (edit.isNew || edit.mode === 'edit');

  const money = (v: unknown) =>
    v === null || v === undefined
      ? '—'
      : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          {/* The source's own title and subtitle. */}
          <h1 className="pd-page-header__title">Projects Management</h1>
          <p className="pd-page-header__subtitle">
            Manage and organize projects for opportunity tracking
          </p>
        </div>
        {/*
          No count and no "New Project" here — adding is the quick-actions row's job and
          the count reads "N records" on the filter row, as the source has it.
        */}
        <div className="pd-page-header__actions">
          <HelpPanel help={HELP} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      {/*
        QUICK ACTIONS — the source's own five buttons and their colours: Add #1976d2,
        Refresh #607d8b, Download Template #f57c00, Upload #388e3c, Download Projects #1565c0.
        The Cards/Details toggle and the size slider sit on the right of the same row.
      */}
      <div className="pd-quickactions">
        <div className="pd-quickactions-left">
          <button type="button" className="pd-qa pd-qa--add" onClick={() => setEdit({ ...EMPTY })}>
            ➕ Add Project
          </button>
          <button type="button" className="pd-qa pd-qa--refresh" onClick={() => void load()}>
            🔄 Refresh
          </button>
          <a className="pd-qa pd-qa--template" href="/api/portfolio_definition/projects/download-template">
            📥 Download Template
          </a>
          <button
            type="button"
            className="pd-qa pd-qa--upload"
            title="Upload projects from Excel file"
            onClick={() => uploadRef.current?.click()}
          >
            📤 Upload Projects
          </button>
          <input
            ref={uploadRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
          <a className="pd-qa pd-qa--download" href="/api/portfolio_definition/projects/download-all">
            📊 Download Projects
          </a>
        </div>

        <div className="pd-quickactions-right">
          <div className="pd-viewtoggle">
            <button
              type="button"
              className={viewMode === 'card' ? 'on' : ''}
              title="Card View"
              onClick={() => setViewMode('card')}
            >
              🎯 Cards
            </button>
            <button
              type="button"
              className={viewMode === 'details' ? 'on' : ''}
              title="Details View"
              onClick={() => setViewMode('details')}
            >
              📋 Details
            </button>
          </div>
          {viewMode === 'card' ? (
            <div className="pd-cardsize">
              <label htmlFor="prj-cardsize">Card Size:</label>
              <input
                id="prj-cardsize"
                type="range"
                min={CARD_MIN}
                max={CARD_MAX}
                step={CARD_STEP}
                value={cardSize}
                onChange={(e) => setCardSize(Number(e.target.value))}
              />
              <span>{cardSize}px</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="pd-filters">
        {/* Projects — a multi-select, Ctrl+click for several. The source's own dropdown. */}
        <div className="pd-mdd">
          <label className="pd-mdd-label">Projects:</label>
          <div className="pd-mdd-box">
            <input
              type="text"
              className="pd-mdd-input"
              readOnly
              value={
                selectedProjects.length === 0
                  ? 'Select Projects'
                  : selectedProjects.length === 1
                    ? selectedProjects[0]!
                    : `${selectedProjects.length} projects selected`
              }
              onClick={(e) => {
                e.stopPropagation();
                setProjectMenuOpen((o) => !o);
              }}
            />
            <span className="pd-mdd-arrow">▼</span>
            {projectMenuOpen ? (
              <div className="pd-mdd-menu">
                {projects.length === 0 ? (
                  <div className="pd-mdd-empty">No projects available</div>
                ) : (
                  projects.map((pr) => {
                    const on = selectedProjects.includes(pr.project_id);
                    return (
                      <div
                        key={pr.project_id}
                        className={`pd-mdd-item${on ? ' selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const multi = e.ctrlKey || e.metaKey;
                          setSelectedProjects((cur) => {
                            if (!multi) return on && cur.length === 1 ? [] : [pr.project_id];
                            return on
                              ? cur.filter((x) => x !== pr.project_id)
                              : [...cur, pr.project_id];
                          });
                        }}
                      >
                        {pr.project_id} - {pr.project_description}
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
          <span className="pd-mdd-hint">(Ctrl+click for multiple)</span>
        </div>

        <label className="pd-mdd-label">Search:</label>
        <input
          type="text"
          placeholder="Project ID or Description..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="Created">Created</option>
          <option value="Active">Active</option>
          <option value="Complete">Complete</option>
        </select>
        <button
          type="button"
          className="pd-btn"
          onClick={() => {
            setFilter('');
            setStatusFilter('');
            setSelectedProjects([]);
            setCurrentView('');
          }}
        >
          Clear
        </button>

        <button type="button" className="pd-btn pd-btn--primary" onClick={() => setSaveOpen(true)}>
          Save View
        </button>
        <label className="pd-mdd-label">Saved Views:</label>
        <select
          value={currentView}
          onChange={(e) => {
            setCurrentView(e.target.value);
            const v = savedViews.find((x) => String(x.view_id) === e.target.value);
            if (v) applyView(v);
          }}
        >
          <option value="">-- Select View --</option>
          {savedViews.map((v) => (
            <option key={v.view_id} value={v.view_id}>
              {v.view_name}
              {v.is_default === 1 ? ' ★' : ''}
            </option>
          ))}
        </select>
        <button type="button" className="pd-btn pd-btn--danger" onClick={() => void deleteView()}>
          Delete
        </button>

        <span className="pd-records">{shown.length} records</span>
      </div>

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : viewMode === 'card' ? (
        <div
          className="pd-cardgrid"
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))` }}
        >
          {shown.map((pr) => {
            const st = stats[pr.project_id];
            const total = Number(st?.total_opportunities ?? 0);
            /* 0 relevant means "nothing to score", which the source shows as N/A, not 0/0. */
            const relevant = Number(st?.relevant_count) || 0;
            const pill = CARD_STATUS[pr.status ?? 'Created'] ?? { bg: '#f5f5f5', fg: '#999' };
            const from = shortDate(pr.effective_from_date);
            const to = shortDate(pr.effective_to_date);
            return (
              <div className="pd-prjcard" key={pr.project_id}>
                {/* SECTION 1 — header, 52px fixed. */}
                <div className="pd-prjcard-head">
                  <div className="pd-prjcard-id">{pr.project_id}</div>
                  <span
                    className="pd-prjcard-status"
                    style={{ background: pill.bg, color: pill.fg }}
                  >
                    {pr.status || 'Created'}
                  </span>
                </div>

                {/* SECTION 2 — title + description, 130px fixed, clipped at four lines. */}
                <div className="pd-prjcard-desc">
                  <div className="pd-prjcard-title">{pr.project_description}</div>
                  {pr.additional_description ? (
                    <div className="pd-prjcard-sub">{pr.additional_description}</div>
                  ) : null}
                </div>

                {/* SECTION 3 — progress tracking. */}
                <div className="pd-prjcard-progress">
                  <div className="pd-prjcard-progresshead">
                    <span className="pd-prjcard-progresslbl">📊 Progress Tracking</span>
                    <span className="pd-prjcard-oppcount">
                      {total} opportunit{total === 1 ? 'y' : 'ies'}
                    </span>
                  </div>

                  <ProgressTrack
                    label="📝 Approved"
                    count={Number(st?.approved_count ?? 0)}
                    total={total}
                  />
                  <ProgressTrack
                    label="🎯 Scored"
                    count={Number(st?.scored_count ?? 0)}
                    total={relevant}
                    na={relevant === 0}
                  />
                  <ProgressTrack
                    label="✅ Committed"
                    count={Number(st?.committed_count ?? 0)}
                    total={total}
                  />
                  <ProgressTrack
                    label="🔧 In Development"
                    count={Number(st?.in_development_count ?? 0)}
                    total={total}
                  />
                  <ProgressTrack
                    label="🚀 Delivered"
                    count={Number(st?.delivered_count ?? 0)}
                    total={total}
                  />

                  {/* Budget utilisation, on the same segmented bar but in money. */}
                  <ProgressTrack
                    label="💰 Budget Utilization"
                    count={Number(st?.budget_utilized ?? 0)}
                    total={Number(st?.project_budget ?? 0)}
                    money
                  />
                </div>

                {/* SECTION 4 — details, 115px fixed. */}
                <div className="pd-prjcard-details">
                  <div>
                    <div className="pd-prjcard-lbl">Budget</div>
                    <div className="pd-prjcard-val">
                      {pr.budget
                        ? `$${Number(pr.budget).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                        : <span className="pd-prjcard-dash">N/A</span>}
                    </div>
                  </div>
                  <div>
                    <div className="pd-prjcard-lbl">Owner</div>
                    <div className="pd-prjcard-val own">
                      {pr.project_owner || <span className="pd-prjcard-dash">-</span>}
                    </div>
                  </div>
                  <div>
                    <div className="pd-prjcard-lbl">Start</div>
                    <div className="pd-prjcard-val sm">
                      {from || <span className="pd-prjcard-dash">-</span>}
                    </div>
                  </div>
                  <div>
                    <div className="pd-prjcard-lbl">End</div>
                    <div className="pd-prjcard-val sm">
                      {to || <span className="pd-prjcard-dash">-</span>}
                    </div>
                  </div>
                  <div />
                </div>

                {/* SECTION 5 — actions, 44px fixed. Delete only when not Active. */}
                <div className="pd-prjcard-actions">
                  <button
                    type="button"
                    className="pd-icon-btn"
                    title="View"
                    onClick={() => openPanel(pr, 'view')}
                  >
                    👁️
                  </button>
                  <button
                    type="button"
                    className="pd-icon-btn"
                    title="Edit"
                    onClick={() => openPanel(pr, 'edit')}
                  >
                    ✏️
                  </button>
                  {isAdmin && pr.status !== 'Active' ? (
                    <button
                      type="button"
                      className="pd-icon-btn"
                      title="Delete"
                      onClick={() => void removeProject(pr.project_id)}
                    >
                      🗑️
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
          {shown.length === 0 ? (
            <div className="pd-empty">No projects match these filters.</div>
          ) : null}
        </div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Project ID</th>
                <th style={{ width: 280 }}>Description</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 130 }}>Budget</th>
                <th style={{ width: 120 }}>From</th>
                <th style={{ width: 120 }}>To</th>
                <th style={{ width: 120 }}>Methodology</th>
                <th style={{ width: 170 }}>Owner</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={9} className="pd-empty">
                    {projects.length === 0
                      ? 'No projects yet. Create one to get started.'
                      : 'No projects match this filter.'}
                  </td>
                </tr>
              ) : (
                shown.map((p) => (
                  <tr key={p.project_id}>
                    <td>{p.project_id}</td>
                    <td>{p.project_description}</td>
                    <td>
                      <span
                        className="pd-badge"
                        style={{ color: STATUS_COLOURS[p.status] ?? '#64748b' }}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="pd-num">{money(p.budget)}</td>
                    <td>{(p.effective_from_date as string)?.slice(0, 10) ?? '—'}</td>
                    <td>{(p.effective_to_date as string)?.slice(0, 10) ?? '—'}</td>
                    <td>{p.methodology ?? '—'}</td>
                    <td>{p.project_owner ?? (p.created_by_name as string) ?? '—'}</td>
                    <td>
                      {/* View / Edit / Delete — the source's three, in its order. */}
                      <div className="pd-row-actions">
                        <button
                          type="button"
                          className="pd-icon-btn"
                          title="View"
                          onClick={() => openPanel(p, 'view')}
                        >
                          👁️
                        </button>
                        <button
                          type="button"
                          className="pd-icon-btn"
                          title="Edit"
                          onClick={() => openPanel(p, 'edit')}
                        >
                          ✏️
                        </button>
                        {isAdmin && p.status !== 'Active' ? (
                          <button
                            type="button"
                            className="pd-icon-btn"
                            title="Delete"
                            onClick={() => void removeProject(p.project_id)}
                          >
                            🗑️
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {edit ? (
        <aside
          className={`pd-panel${maximized ? ' pd-panel--max' : ''}`}
          role="dialog"
          aria-label={edit.isNew ? 'New project' : `Project ${edit.project_id}`}
        >
          <div className="pd-panel__head">
            <div className="pd-panel__ident">
              <h2 className="pd-panel__title">
                {edit.isNew ? 'New Project' : edit.project_description || edit.project_id}
              </h2>
              <p className="pd-panel__sub">{edit.isNew ? 'Not yet saved' : edit.project_id}</p>
            </div>
            {/*
              One position for the controls, always: top right, on the title's row. Save
              appears only when the panel is editable — there is no Edit control here,
              because the pencil in the row is how you get to edit mode.
            */}
            <div className="pd-panel__ctls">
              <button
                type="button"
                className="pd-panel__ctl pd-panel__ctl--back"
                onClick={closePanel}
              >
                ‹ Back
              </button>
              {editable ? (
                <button
                  type="button"
                  className="pd-panel__ctl pd-panel__ctl--save"
                  title="Save changes"
                  onClick={save}
                >
                  💾
                </button>
              ) : null}
              <button
                type="button"
                className="pd-panel__ctl pd-panel__ctl--icon"
                title={maximized ? 'Restore' : 'Maximize'}
                onClick={() => setMaximized((m) => !m)}
              >
                {maximized ? '⤡' : '⤢'}
              </button>
              <button
                type="button"
                className="pd-panel__ctl pd-panel__ctl--close"
                title="Close"
                onClick={closePanel}
              >
                ✕
              </button>
            </div>
          </div>

          <div className="pd-panel__body">
            <div className="pd-panel-card">
              <h3 className="pd-panel-card__title">Project Details</h3>
              <div className="pd-panel-grid">
                <div className="pd-panel-field">
                  <label className="pd-panel-label" htmlFor="pd-pid">
                    Project ID{editable ? ' *' : ''}
                  </label>
                  {editable ? (
                    <input
                      id="pd-pid"
                      className="pd-panel-input"
                      type="text"
                      value={edit.project_id}
                      readOnly={!edit.isNew}
                      onChange={(e) => setEdit({ ...edit, project_id: e.target.value })}
                    />
                  ) : (
                    <div className="pd-panel-read">{edit.project_id || '—'}</div>
                  )}
                </div>

                <div className="pd-panel-field">
                  <label className="pd-panel-label" htmlFor="pd-pstatus">
                    Status
                  </label>
                  {editable ? (
                    <>
                      <select
                        id="pd-pstatus"
                        className="pd-panel-input"
                        value={edit.status}
                        onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                      >
                        <option value="Created">Created</option>
                        <option value="Active">Active</option>
                        <option value="Complete">Complete</option>
                      </select>
                      <span className="pd-panel-hint">
                        Owner or administrator only. Complete requires every scorable
                        opportunity to be scored.
                      </span>
                    </>
                  ) : (
                    <div className="pd-panel-read">{edit.status || '—'}</div>
                  )}
                </div>

                <div className="pd-panel-field pd-panel-field--wide">
                  <label className="pd-panel-label" htmlFor="pd-pdesc">
                    Project Description{editable ? ' *' : ''}
                  </label>
                  {editable ? (
                    <input
                      id="pd-pdesc"
                      className="pd-panel-input"
                      type="text"
                      value={edit.project_description}
                      onChange={(e) => setEdit({ ...edit, project_description: e.target.value })}
                    />
                  ) : (
                    <div className="pd-panel-read">{edit.project_description || '—'}</div>
                  )}
                </div>

                <div className="pd-panel-field pd-panel-field--wide">
                  <label className="pd-panel-label" htmlFor="pd-padd">
                    Additional Description
                  </label>
                  {editable ? (
                    <textarea
                      id="pd-padd"
                      className="pd-panel-input"
                      value={edit.additional_description}
                      onChange={(e) =>
                        setEdit({ ...edit, additional_description: e.target.value })
                      }
                    />
                  ) : (
                    <div className="pd-panel-read">{edit.additional_description || '—'}</div>
                  )}
                </div>

                {/*
                 * SCORING FRAMEWORK. Owner-directed, 2026-08-10, and placed here by his
                 * instruction — "below Additional Description field".
                 *
                 * This is the link that makes scoring possible. Its two rules are his:
                 * only an ACTIVE project may be assigned one, and only a framework in
                 * "Design Complete" may be chosen. Both are enforced again on the server;
                 * the disabled control and the hint below exist so the reason is visible
                 * rather than arriving as a rejected save.
                 *
                 * Assigning it stamps every opportunity in the project that has not been
                 * scored yet — and anything added later inherits it.
                 */}
                <div className="pd-panel-field pd-panel-field--wide">
                  <label className="pd-panel-label" htmlFor="pd-pfw">
                    Scoring Framework
                  </label>
                  {editable ? (
                    <>
                      <select
                        id="pd-pfw"
                        className="pd-panel-input"
                        value={edit.framework_id}
                        disabled={edit.status !== 'Active'}
                        onChange={(e) => setEdit({ ...edit, framework_id: e.target.value })}
                      >
                        <option value="">— none —</option>
                        {completeFrameworks.map((f) => (
                          <option key={f.framework_id} value={f.framework_id}>
                            {f.framework_name} ({f.framework_id})
                          </option>
                        ))}
                      </select>
                      {/*
                       * WHY A DISABLED CONTROL NEEDS A LOUD REASON.
                       *
                       * The first person to use this screen went to project 3639, found the
                       * dropdown dead, and had no idea why — the explanation was sitting
                       * right underneath in 11px #94a3b8, which the eye skips straight past
                       * when it has already landed on a greyed-out control.
                       *
                       * So a BLOCKING reason renders in the module's amber, at readable
                       * size, and the merely-informational note keeps the quiet hint style.
                       * A control that refuses to work has to say so louder than a control
                       * that is simply explaining itself.
                       */}
                      {edit.status !== 'Active' ? (
                        <span className="pd-panel-hint pd-panel-hint--blocked">
                          Set <strong>Status</strong> to <strong>Active</strong> above to
                          assign a framework. Projects that are not Active cannot be scored.
                        </span>
                      ) : completeFrameworks.length === 0 ? (
                        <span className="pd-panel-hint pd-panel-hint--blocked">
                          No framework is in <strong>Design Complete</strong> status yet.
                          Complete one under Settings → Assessment Framework → Frameworks.
                        </span>
                      ) : (
                        <span className="pd-panel-hint">
                          Opportunities in this project inherit this framework.
                          Already-scored opportunities keep the framework they were scored
                          against.
                        </span>
                      )}
                    </>
                  ) : (
                    <div className="pd-panel-read">
                      {edit.framework_id
                        ? `${frameworkName(edit.framework_id)} (${edit.framework_id})`
                        : '—'}
                    </div>
                  )}
                </div>

                <div className="pd-panel-field">
                  <label className="pd-panel-label" htmlFor="pd-pmeth">
                    Methodology
                  </label>
                  {editable ? (
                    <select
                      id="pd-pmeth"
                      className="pd-panel-input"
                      value={edit.methodology}
                      onChange={(e) => setEdit({ ...edit, methodology: e.target.value })}
                    >
                      <option value="Waterfall">Waterfall</option>
                      <option value="Agile">Agile</option>
                    </select>
                  ) : (
                    <div className="pd-panel-read">{edit.methodology || '—'}</div>
                  )}
                </div>

                <div className="pd-panel-field">
                  <label className="pd-panel-label" htmlFor="pd-powner">
                    Project Owner
                  </label>
                  {editable ? (
                    <input
                      id="pd-powner"
                      className="pd-panel-input"
                      type="text"
                      value={edit.project_owner}
                      onChange={(e) => setEdit({ ...edit, project_owner: e.target.value })}
                    />
                  ) : (
                    <div className="pd-panel-read">{edit.project_owner || '—'}</div>
                  )}
                </div>

                <div className="pd-panel-field">
                  <label className="pd-panel-label" htmlFor="pd-pfrom">
                    Effective From
                  </label>
                  {editable ? (
                    <input
                      id="pd-pfrom"
                      className="pd-panel-input"
                      type="date"
                      value={edit.effective_from_date}
                      onChange={(e) => setEdit({ ...edit, effective_from_date: e.target.value })}
                    />
                  ) : (
                    <div className="pd-panel-read">{edit.effective_from_date || '—'}</div>
                  )}
                </div>

                <div className="pd-panel-field">
                  <label className="pd-panel-label" htmlFor="pd-pto">
                    Effective To
                  </label>
                  {editable ? (
                    <input
                      id="pd-pto"
                      className="pd-panel-input"
                      type="date"
                      value={edit.effective_to_date}
                      onChange={(e) => setEdit({ ...edit, effective_to_date: e.target.value })}
                    />
                  ) : (
                    <div className="pd-panel-read">{edit.effective_to_date || '—'}</div>
                  )}
                </div>

                <div className="pd-panel-field">
                  <label className="pd-panel-label" htmlFor="pd-pcreated">
                    Created By
                  </label>
                  {editable ? (
                    <select
                      id="pd-pcreated"
                      className="pd-panel-input"
                      value={edit.created_by}
                      onChange={(e) =>
                        setEdit({
                          ...edit,
                          created_by: e.target.value ? Number(e.target.value) : '',
                        })
                      }
                    >
                      <option value="">— you —</option>
                      {users.map((u) => (
                        <option key={u.user_id} value={u.user_id}>
                          {u.full_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="pd-panel-read">
                      {users.find((u) => u.user_id === edit.created_by)?.full_name || '—'}
                    </div>
                  )}
                </div>

                <div className="pd-panel-field">
                  <label className="pd-panel-label">Budget</label>
                  <div className="pd-panel-read">
                    {money(projects.find((x) => x.project_id === edit.project_id)?.budget)}
                  </div>
                  <span className="pd-panel-hint">
                    Rolled up from this project&apos;s opportunities — not editable here.
                  </span>
                </div>
              </div>
            </div>

            {/*
             * CLASSIFICATION — its OWN section, after Project Details.
             *
             * This is the source's structure, not a new one: `views/projects-edit.ejs:90`
             * carries the comment "Classification — its own section between Project Details
             * and Planning", holding a single free-form Category. The owner asked for the
             * same placement, and the field, maxlength and placeholder are the source's.
             *
             * NOTE this panel has no separate "Planning" card — the source splits Project
             * Details / Classification / Planning into three, whereas this port put every
             * field in one card during the original clone. Classification therefore sits
             * after Project Details, which is where it belongs, but there is nothing after
             * it. Splitting the rest to match the source is a bigger change than was asked
             * for, so it is RAISED rather than done.
             */}
            <div className="pd-panel-card">
              <h3 className="pd-panel-card__title">Classification</h3>
              <div className="pd-panel-grid">
                <div className="pd-panel-field pd-panel-field--wide">
                  <label className="pd-panel-label" htmlFor="pd-pcategory">
                    Category
                  </label>
                  {editable ? (
                    <>
                      <input
                        id="pd-pcategory"
                        className="pd-panel-input"
                        type="text"
                        maxLength={100}
                        placeholder="e.g. Reporting, Automation, Data Quality"
                        value={edit.category}
                        onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                      />
                      <span className="pd-panel-hint">
                        Free text. Used to group and filter projects.
                      </span>
                    </>
                  ) : (
                    <div className="pd-panel-read">{edit.category || '—'}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </aside>
      ) : null}

      {/* Save Filter View — the source's own modal, its wording and its default tick. */}
      {saveOpen ? (
        <div className="pd-saveview-backdrop" onClick={() => setSaveOpen(false)}>
          <div className="pd-saveview" onClick={(e) => e.stopPropagation()}>
            <h3>Save Filter View</h3>
            <div className="pd-saveview-field">
              <label htmlFor="prj-viewname">View Name:</label>
              <input
                id="prj-viewname"
                type="text"
                placeholder="Enter view name..."
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                autoFocus
              />
            </div>
            <label className="pd-saveview-default">
              <input
                type="checkbox"
                checked={viewIsDefault}
                onChange={(e) => setViewIsDefault(e.target.checked)}
              />
              <span>Set as default view for this screen</span>
            </label>
            <div className="pd-saveview-actions">
              <button type="button" className="pd-btn" onClick={() => setSaveOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                onClick={() => void saveView()}
              >
                Save View
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
